import type { useTranslations } from "use-intl";
import z, {
  array,
  date,
  literal,
  number,
  object,
  string,
  union,
  null as zodNull,
  ZodIssueCode,
  coerce,
} from "zod";

import { MIN_ADULT_AGE } from "@/utils/constants";
import { citySchema } from "@/utils/locations";

export const getDatesValidationSchema = (
  t: ReturnType<typeof useTranslations>,
) =>
  object({
    checkIn: date({ required_error: t("hotels.validation.dates.checkIn") }),
    checkOut: date({ required_error: t("hotels.validation.dates.checkOut") }),
  }).superRefine((values, ctx) => {
    const { checkIn, checkOut } = values;

    if (checkOut < checkIn) {
      ctx.addIssue({
        code: "custom",
        path: ["checkOut"],
        message: t("hotels.validation.dates.min"),
      });
    }
  });

export const getRoomsValidationSchema = (
  t: ReturnType<typeof useTranslations>,
) =>
  array(
    object({
      adults: number().min(1),
      children: number().min(0),
      childrenAges: array(
        number()
          .int()
          .min(0)
          .max(MIN_ADULT_AGE - 1),
      ),
    }),
  )
    .min(1, t("hotels.validation.roomsMin"))
    .max(5, t("hotels.validation.roomsMax"));

export const getHotelSearchFormInputSchema = (
  t: ReturnType<typeof useTranslations>,
) =>
  object({
    destination: citySchema.nullable(),
    dates: object({
      checkIn: coerce.date(),
      checkOut: coerce.date(),
    }),
    rooms: getRoomsValidationSchema(t),
  });
export type HotelSearchFormInputValues = z.infer<
  ReturnType<typeof getHotelSearchFormInputSchema>
>;

export const getHotelSearchFormSchema = (
  t: ReturnType<typeof useTranslations>,
) =>
  object({
    destination: union([
      object({
        id: string().min(1).regex(/^\d+$/),
        name: string().min(1, t("hotels.validation.destination")),
        code: string().optional(),
        countryName: string(),
        countryCode: string().optional(),
        type: literal("CITY"),
      }),
      zodNull(),
    ]).superRefine((v, ctx) => {
      if (v === null) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          message: t("hotels.validation.destination"),
        });
      }
    }),
    dates: getDatesValidationSchema(t),
    rooms: getRoomsValidationSchema(t),
  });

export type HotelSearchFormValues = z.infer<
  ReturnType<typeof getHotelSearchFormSchema>
>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const typecheck: z.input<ReturnType<typeof getHotelSearchFormSchema>> =
  {} as HotelSearchFormInputValues;
